import * as React from 'react'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { useIsLoggedInQuery, userQueryKey } from '@repo/cf-user/hooks/auth'
import type { Language } from 'codegen/types'
import { defaultLanguage } from 'cf-i18n/constants'
import { useStudioLangCookie } from 'cf-i18n/cookies'
import { useUpdateUserLanguageMutation } from 'codegen/generated/user-preferences'
import type { UserQuery } from '@repo/cf-user/gen/user'
import { useReloadLanguage } from './use-reload-language'
import { publishStudioLanguageChange } from './use-studio-language-broadcast'
import { useNavigateToCanonicalLocaleUrl } from './use-sync-locale-url'

/** Shared so a switch can tell whether another one is still in flight. */
const changeLanguageMutationKey = ['studio', 'change-language'] as const

export function useChangeLanguageMutation() {
  const reloadLanguage = useReloadLanguage()
  const [cookieLanguage, setLangCookie] = useStudioLangCookie()
  const navigateToCanonicalLocaleUrl = useNavigateToCanonicalLocaleUrl()
  const { data: isLoggedIn } = useIsLoggedInQuery()
  const updateUserLanguageMutation = useUpdateUserLanguageMutation()
  const queryClient = useQueryClient()

  /** Tells the switch a reader is waiting on from ones they have replaced. */
  const latestSwitchRef = React.useRef(0)
  /** Where a rejected switch returns to: the last language the account held. */
  const storedLanguageRef = React.useRef<Language | undefined>(undefined)

  /**
   * Everything a language switch changes in the browser. Called with the picked
   * language to apply it and with the previous one to undo it, so the two paths
   * cannot drift.
   *
   * Only the language is patched into the cached user rather than restoring a
   * whole snapshot, so a rollback cannot revert unrelated fields that were
   * refetched in the meantime.
   */
  async function applyLanguage(language: Language) {
    queryClient.setQueryData<UserQuery>(userQueryKey, user => {
      if (!user?.me?.user) {
        return user
      }

      return { ...user, me: { ...user.me, user: { ...user.me.user, language } } }
    })

    // Cookie must update before i18n/URL sync so URL resolution does not briefly
    // read the previous language and fight the picker (lang/title flicker).
    setLangCookie(language)

    // Announced after the write, so a tab that reacts immediately reads the new
    // value rather than racing the cookie.
    publishStudioLanguageChange()

    try {
      await reloadLanguage(language)
    } catch {
      // Reloading resources is a local refresh that `useSyncI18nLanguage` performs
      // again on the next render, so a failed chunk must not abort the switch: the
      // preference is already recorded and the account write still has to be sent.
    } finally {
      // `useSyncLocaleUrl` rewrites the prefix from the cookie on its own, so this
      // is only about turning the URL over in the same beat as the strings.
      navigateToCanonicalLocaleUrl(language)
    }
  }

  return useMutation({
    mutationKey: changeLanguageMutationKey,
    /**
     * The switch a reader can see — strings, cookie, URL, other tabs — is applied
     * before the account write is sent, so the picker answers at local speed
     * instead of at the speed of the round trip.
     */
    async onMutate({ language }: { language: Language }) {
      // A user fetch already in flight resolved against the previous language, so
      // letting it land would overwrite what we are about to apply.
      await queryClient.cancelQueries({ queryKey: userQueryKey })

      // `isMutating` already counts this one, so 1 means nothing else is in
      // flight and the language on screen is the one the account holds. Reading it
      // only then keeps a reader who reselects mid-flight from rolling back to an
      // optimistic language the account never stored.
      if (queryClient.isMutating({ mutationKey: changeLanguageMutationKey }) === 1) {
        storedLanguageRef.current =
          queryClient.getQueryData<UserQuery>(userQueryKey)?.me?.user?.language ??
          cookieLanguage ??
          defaultLanguage
      }

      const switchId = ++latestSwitchRef.current

      await applyLanguage(language)

      return { switchId }
    },
    async mutationFn({ language }: { language: Language }) {
      if (!isLoggedIn) {
        return
      }

      const { usersUpdateLanguage } = await updateUserLanguageMutation.mutateAsync({ language })

      // The account is the source of truth once you are logged in, so a failed
      // write must not leave the UI translated: the next reload would snap back to
      // the stored language and look like the picker reset itself.
      if (!usersUpdateLanguage.success) {
        throw new Error(
          usersUpdateLanguage.errors?.map(error => error.message).join(', ') ||
            `Could not save the ${language} language to this account`,
        )
      }
    },
    onSuccess(_data, { language }) {
      // The account took this one, so it is what a later rejection returns to —
      // not the language that was on screen when the reader started switching.
      storedLanguageRef.current = language
    },
    async onError(_error, _variables, context) {
      // A newer selection has already replaced this one. Rolling back would drag
      // the reader off the language they are now looking at, and would leave the
      // cookie disagreeing with the account write that did land.
      if (!context || context.switchId !== latestSwitchRef.current) {
        return
      }

      await applyLanguage(storedLanguageRef.current ?? defaultLanguage)
    },
    async onSettled(_data, _error, _variables, context) {
      // Only the newest switch reconciles with the account: an older one would
      // refetch a language the reader has already moved off. Asking "is anything
      // else in flight?" instead would leave two switches settling in the same
      // tick each deferring to the other, and nothing reconciling at all.
      if (!isLoggedIn || !context || context.switchId !== latestSwitchRef.current) {
        return
      }

      await queryClient.invalidateQueries({ queryKey: userQueryKey })
    },
  })
}
